import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { AdminCard } from '../common/AdminPrimitives';

interface StatCardData {
  title: string;
  value: number;
  unit: string;
  change: string;
  changeType: 'positive' | 'negative' | 'neutral';
}

interface DashboardStats {
  newMembers: StatCardData;
  activeMembers: StatCardData;
  courseStarts: StatCardData;
  averageWatchTime: {
    minutes: number;
    seconds: number;
    change: string;
    changeType: 'positive' | 'negative' | 'neutral';
  };
  newQuestions: StatCardData;
}

const DashboardStatsCards: React.FC = () => {
  const [statsData, setStatsData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async (): Promise<DashboardStats> => {
    const baseUrl = process.env.REACT_APP_CAMPUS_API_BASE_URL;
    const today = new Date();
    const todayKST = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const responses = await Promise.all([
      fetch(`${baseUrl}/api/admin/new-member-count/today`),
      fetch(`${baseUrl}/api/admin/active-member-count/today`),
      fetch(`${baseUrl}/api/admin/average-watch-time/today`),
      fetch(`${baseUrl}/api/admin/new-questions-count/today`),
      fetch(`${baseUrl}/api/admin/course-start-count?startDate=${todayKST}&endDate=${todayKST}`),
    ]);

    const data = await Promise.all(responses.map((response) => response.json()));

    const calculateChange = (todayValue: number, yesterdayValue: number) => {
      if (yesterdayValue === 0) {
        if (todayValue === 0) {
          return { change: '0%', type: 'neutral' as const };
        }

        return { change: '+100%', type: 'positive' as const };
      }

      const pct = ((todayValue - yesterdayValue) / yesterdayValue) * 100;

      if (pct > 0) {
        return { change: `+${pct.toFixed(1)}%`, type: 'positive' as const };
      }

      if (pct < 0) {
        return { change: `${pct.toFixed(1)}%`, type: 'negative' as const };
      }

      return { change: '0%', type: 'neutral' as const };
    };

    const newMembersToday = data[0]?.data?.today || 0;
    const newMembersYesterday = data[0]?.data?.yesterday || 0;
    const activeMembersToday = data[1]?.data?.today || 0;
    const activeMembersYesterday = data[1]?.data?.yesterday || 0;
    const watchTimeToday = data[2]?.data?.today || 0;
    const watchTimeYesterday = data[2]?.data?.yesterday || 0;
    const questionsToday = data[3]?.data?.today || 0;
    const questionsYesterday = data[3]?.data?.yesterday || 0;
    const courseStartsToday = data[4]?.data?.[0]?.count || 0;

    const minutes = Math.floor(watchTimeToday);
    const seconds = Math.round((watchTimeToday - minutes) * 60);

    const newMembersChange = calculateChange(newMembersToday, newMembersYesterday);
    const activeMembersChange = calculateChange(activeMembersToday, activeMembersYesterday);
    const watchTimeChange = calculateChange(watchTimeToday, watchTimeYesterday);
    const questionsChange = calculateChange(questionsToday, questionsYesterday);
    const courseStartsChange = calculateChange(courseStartsToday, 0);

    return {
      newMembers: {
        title: '신규 가입자 수',
        value: newMembersToday,
        unit: '명',
        change: newMembersChange.change,
        changeType: newMembersChange.type,
      },
      activeMembers: {
        title: '일일 활성 사용자 수',
        value: activeMembersToday,
        unit: '명',
        change: activeMembersChange.change,
        changeType: activeMembersChange.type,
      },
      courseStarts: {
        title: '수강 시작 수',
        value: courseStartsToday,
        unit: '회',
        change: courseStartsChange.change,
        changeType: courseStartsChange.type,
      },
      averageWatchTime: {
        minutes,
        seconds,
        change: watchTimeChange.change,
        changeType: watchTimeChange.type,
      },
      newQuestions: {
        title: '신규 등록 질문 수',
        value: questionsToday,
        unit: '개',
        change: questionsChange.change,
        changeType: questionsChange.type,
      },
    };
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setStatsData(await fetchDashboardData());
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <StatsGrid>
        {Array.from({ length: 5 }).map((_, index) => (
          <Card key={index}>
            <Heading>로딩 중...</Heading>
            <MetricRow>
              <MetricValue>--</MetricValue>
              <MetricUnit>--</MetricUnit>
            </MetricRow>
            <ChangeRow $tone="neutral">
              <ChangeText>--%</ChangeText>
            </ChangeRow>
          </Card>
        ))}
      </StatsGrid>
    );
  }

  if (!statsData) {
    return <div>대시보드 데이터를 불러오는데 실패했습니다.</div>;
  }

  return (
    <StatsGrid>
      <StatCard stat={statsData.newMembers} />
      <StatCard stat={statsData.activeMembers} />
      <StatCard stat={statsData.courseStarts} />
      <Card>
        <Heading>평균 시청 시간</Heading>
        <TimeRow>
          <MetricRow>
            <MetricValue>{String(statsData.averageWatchTime.minutes).padStart(2, '0')}</MetricValue>
            <MetricUnit>분</MetricUnit>
          </MetricRow>
          <MetricRow>
            <MetricValue>{String(statsData.averageWatchTime.seconds).padStart(2, '0')}</MetricValue>
            <MetricUnit>초</MetricUnit>
          </MetricRow>
        </TimeRow>
        <ChangeRow $tone={statsData.averageWatchTime.changeType}>
          <ChangeText>{statsData.averageWatchTime.change}</ChangeText>
        </ChangeRow>
      </Card>
      <StatCard stat={statsData.newQuestions} />
    </StatsGrid>
  );
};

const StatCard: React.FC<{ stat: StatCardData }> = ({ stat }) => (
  <Card>
    <Heading>{stat.title}</Heading>
    <MetricRow>
      <MetricValue>{stat.value.toLocaleString()}</MetricValue>
      <MetricUnit>{stat.unit}</MetricUnit>
    </MetricRow>
    <ChangeRow $tone={stat.changeType}>
      <ChangeText>{stat.change}</ChangeText>
    </ChangeRow>
  </Card>
);

const StatsGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(AdminCard)`
  display: flex;
  min-height: 164px;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
`;

const Heading = styled.div`
  color: #171719;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MetricValue = styled.b`
  color: #296768;
  font-size: 36px;
  font-weight: 700;
  line-height: 1.33;
  letter-spacing: -0.03em;
`;

const MetricUnit = styled.div`
  color: #296768;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
`;

const ChangeRow = styled.div<{ $tone: 'positive' | 'negative' | 'neutral' }>`
  display: flex;
  align-items: center;
  gap: 8px;

  ${({ $tone }) =>
    $tone === 'negative'
      ? css`
          color: #ff4242;
        `
      : $tone === 'neutral'
        ? css`
            color: rgba(55, 56, 60, 0.61);
          `
        : css`
            color: #00bf40;
          `}
`;

const ChangeText = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.01em;
`;

export default DashboardStatsCards;
